/**
 * Comprehensive Activity Utilities
 * Fetches and merges activities from all backend sources
 */

import { supabase, STORAGE_BUCKETS } from '../lib/supabase.js';

/**
 * Convert storage path to public URL if needed
 * @param {string} url - The URL or path
 * @param {string} bucket - The storage bucket name
 * @returns {string} - Full public URL
 */
function convertToPublicUrl(url, bucket = 'nft-previews') {
  if (!url) return null;
  
  // If already a full URL, return as-is
  if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('data:')) {
    return url;
  }
  
  // Convert relative path to public URL
  try {
    const { data } = supabase.storage
      .from(bucket)
      .getPublicUrl(url);
    return data?.publicUrl || url;
  } catch (error) {
    console.error('Error converting to public URL:', error);
    return url;
  }
}

/**
 * Format timestamp to relative time (e.g., "2h ago", "1d ago")
 * @param {number|string|Date} timestamp - Unix timestamp, ISO string, or Date object
 * @returns {string} Formatted relative time
 */
export function formatTimeAgo(timestamp) {
  if (!timestamp) return 'Unknown';
  
  const now = Date.now();
  let time;
  
  // Handle different timestamp formats
  if (typeof timestamp === 'number') {
    // Unix timestamp - could be in seconds or milliseconds
    time = timestamp < 10000000000 ? timestamp * 1000 : timestamp;
  } else if (typeof timestamp === 'string') {
    // ISO string
    time = new Date(timestamp).getTime();
  } else if (timestamp instanceof Date) {
    time = timestamp.getTime();
  } else {
    return 'Unknown';
  }
  
  const diffMs = now - time;
  
  // If timestamp is in the future, return "Just now"
  if (diffMs < 0) return 'Just now';
  
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMins = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);
  const diffWeeks = Math.floor(diffDays / 7);
  const diffMonths = Math.floor(diffDays / 30);
  
  // Show "Just now" for first 60 seconds
  if (diffSeconds < 60) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  if (diffWeeks < 4) return `${diffWeeks}w ago`;
  if (diffMonths < 12) return `${diffMonths}mo ago`;
  
  return new Date(time).toLocaleDateString();
}

/**
 * Consolidate mint events with the same piece name
 * When multiple pieces are minted with quantity > 1, they create multiple records
 * with the same name but different mint addresses
 * This function groups them into a single activity with the combined quantity
 * @param {Array} activities - Array of activity objects
 * @returns {Array} Array with consolidated mint events
 */
function consolidateMintEvents(activities) {
  const consolidated = [];
  const processedIndices = new Set();

  // Sort activities by timestamp first (most recent first)
  const sortedActivities = [...activities].sort((a, b) => b.timestamp - a.timestamp);

  sortedActivities.forEach((activity, index) => {
    // Skip if already processed
    if (processedIndices.has(index)) return;

    // Consolidate PIECE_CREATED (mint) and PIECE_LISTED events
    const isConsolidatableType = activity.type === 'PIECE_CREATED' || activity.type === 'PIECE_LISTED';
    
    if (isConsolidatableType && activity.pieceName) {
      const user = activity.user || activity.from || activity.to || 'unknown';
      const group = {
        ...activity,
        count: 1,
        mintAddresses: [activity.mintAddress],
        originalActivities: [activity],
        earliestTimestamp: activity.timestamp,
        latestTimestamp: activity.timestamp
      };
      
      processedIndices.add(index);

      // Keep looking for similar activities until we can't find any more
      // This handles sequential mints/listings that happen with delays between them
      let foundNewMatch = true;
      while (foundNewMatch) {
        foundNewMatch = false;
        
        for (let j = index + 1; j < sortedActivities.length; j++) {
          if (processedIndices.has(j)) continue;
          
          const otherActivity = sortedActivities[j];
          
          // Check time difference against the entire group's time range
          // If this activity is within 60 seconds of ANY activity in the group, include it
          const timeDiffFromEarliest = Math.abs(group.earliestTimestamp - otherActivity.timestamp);
          const timeDiffFromLatest = Math.abs(group.latestTimestamp - otherActivity.timestamp);
          
          // Stop looking if we've gone too far back in time
          if (Math.min(timeDiffFromEarliest, timeDiffFromLatest) > 60 * 1000) {
            // Only break if this activity is older than our earliest
            if (otherActivity.timestamp < group.earliestTimestamp - 60 * 1000) {
              break;
            }
            continue;
          }
          
          // Check if it's the same type of activity (same event type, piece name, and user)
          const otherUser = otherActivity.user || otherActivity.from || otherActivity.to || 'unknown';
          if (
            otherActivity.type === activity.type &&
            otherActivity.pieceName === activity.pieceName &&
            otherUser === user
          ) {
            group.count++;
            group.mintAddresses.push(otherActivity.mintAddress);
            group.originalActivities.push(otherActivity);
            processedIndices.add(j);
            foundNewMatch = true;
            
            // Update the group's time range
            if (otherActivity.timestamp < group.earliestTimestamp) {
              group.earliestTimestamp = otherActivity.timestamp;
              group.timestamp = otherActivity.timestamp;
              group.timeAgo = otherActivity.timeAgo;
            }
            if (otherActivity.timestamp > group.latestTimestamp) {
              group.latestTimestamp = otherActivity.timestamp;
            }
          }
        }
      }

      // Add consolidated group or single activity
      if (group.count > 1) {
        const actionWord = activity.type === 'PIECE_CREATED' ? 'Minted' : 'Listed';
        const actionPast = activity.type === 'PIECE_CREATED' ? 'minted' : 'listed for sale';
        const { earliestTimestamp, latestTimestamp, ...groupWithoutTempFields } = group;
        consolidated.push({
          ...groupWithoutTempFields,
          quantity: `${group.count} pieces`,
          title: `${actionWord} "${group.pieceName}"`,
          description: `${group.count} pieces ${actionPast}`
        });
      } else {
        // Single activity, return as-is (but remove the helper properties)
        const { count, mintAddresses, originalActivities, earliestTimestamp, latestTimestamp, ...singleActivity } = group;
        consolidated.push(singleActivity);
      }
    } else {
      // Not a consolidatable event, keep as-is
      if (!processedIndices.has(index)) {
        consolidated.push(activity);
        processedIndices.add(index);
      }
    }
  });

  // Sort again by timestamp (most recent first)
  consolidated.sort((a, b) => b.timestamp - a.timestamp);
  
  console.log(`🔄 Consolidated ${activities.length} activities into ${consolidated.length} (grouped ${activities.length - consolidated.length} mint events)`);
  
  return consolidated;
}

/**
 * Fetch all recent marketplace activities from multiple sources
 * @param {number} limit - Maximum number of activities to fetch
 * @param {number} offset - Number of activities to skip (for pagination)
 * @returns {Promise<Array>} Array of formatted activity objects
 */
export async function getAllMarketplaceActivities(limit = 50, offset = 0) {
  try {
    console.log(`🔍 Fetching marketplace activities (limit: ${limit}, offset: ${offset})...`);
    
    // Fetch activities from all sources in parallel
    // Note: We fetch more than needed and slice later to handle mixed sources properly
    const fetchLimit = limit + offset + 50; // Extra buffer to ensure we have enough after merge
    
    const [
      nftActivities,
      walletActivities,
      offers,
      trades
    ] = await Promise.all([
      fetchNFTActivities(fetchLimit),
      fetchWalletActivities(fetchLimit),
      fetchOfferActivities(fetchLimit),
      fetchTradeActivities(fetchLimit)
    ]);

    console.log('📊 Activities by source:', {
      nftActivities: nftActivities.length,
      walletActivities: walletActivities.length,
      offers: offers.length,
      trades: trades.length
    });

    // Merge all activities
    const allActivities = [
      ...nftActivities,
      ...walletActivities,
      ...offers,
      ...trades
    ];

    // Sort by timestamp (most recent first)
    allActivities.sort((a, b) => b.timestamp - a.timestamp);

    // Consolidate mint events with multiple quantities
    const consolidatedActivities = consolidateMintEvents(allActivities);

    // Apply offset and limit
    const paginatedActivities = consolidatedActivities.slice(offset, offset + limit);

    console.log(`✅ Found ${paginatedActivities.length} activities (${consolidatedActivities.length} total before pagination, ${allActivities.length} before consolidation)`);
    return paginatedActivities;
  } catch (error) {
    console.error('Error fetching marketplace activities:', error);
    return [];
  }
}

/**
 * Fetch on-chain NFT activities (from Helius webhooks)
 */
async function fetchNFTActivities(limit = 50) {
  try {
    console.log('🔍 Fetching NFT activities from nft_activity table (limit:', limit, ')...');
    
    // Fetch activities - prioritize created_at to ensure new app-generated activities appear first
    // This is critical because app-generated activities have synthetic block_time values
    const { data: activities, error: activitiesError } = await supabase
      .from('nft_activity')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(limit);

    console.log('📊 NFT activities query result:', { 
      count: activities?.length || 0, 
      hasData: !!activities,
      error: activitiesError?.message,
      errorDetails: activitiesError 
    });

    if (activitiesError) {
      console.error('❌ Error fetching NFT activities:', activitiesError);
      return [];
    }

    if (!activities || activities.length === 0) {
      console.log('⚠️ No NFT activities found in database');
      return [];
    }

    console.log('✅ Found', activities.length, 'NFT activity records');
    
    // Log the event types we found
    const eventTypes = activities.reduce((acc, a) => {
      acc[a.event_type] = (acc[a.event_type] || 0) + 1;
      return acc;
    }, {});
    console.log('📊 NFT activity event types:', eventTypes);
    
    // Log most recent 3 activities
    console.log('📊 Most recent NFT activities:', activities.slice(0, 3).map(a => ({
      event_type: a.event_type,
      from: a.from_address?.slice(0, 8),
      to: a.to_address?.slice(0, 8),
      mint: a.mint_address?.slice(0, 8),
      created_at: a.created_at
    })));

    // Fetch executed P2P trades to filter out their individual transfer events
    const { data: executedTrades } = await supabase
      .from('p2p_trades')
      .select('execution_signature')
      .eq('status', 'executed')
      .not('execution_signature', 'is', null);

    // Create a Set of trade execution signatures for fast lookup
    const tradeSignatures = new Set(
      (executedTrades || []).map(t => t.execution_signature).filter(Boolean)
    );

    console.log(`🔄 Found ${tradeSignatures.size} executed trade signatures to filter out:`, Array.from(tradeSignatures));

    // Filter out transfer events that are part of P2P trades
    // These will be shown as consolidated trade activities instead
    const filteredActivities = activities.filter(activity => {
      // Check both tx_sig and signature fields (different activities might use different field names)
      const activitySig = activity.tx_sig || activity.signature;
      
      // If it's a transfer event and its signature matches a trade execution signature, filter it out
      if (activity.event_type === 'transfer' && activitySig) {
        if (tradeSignatures.has(activitySig)) {
          console.log(`🚫 Filtering out transfer for ${activity.mint_address?.slice(0, 8)} (part of P2P trade, sig: ${activitySig?.slice(0, 8)}...)`);
          return false;
        } else {
          console.log(`✓ Keeping transfer for ${activity.mint_address?.slice(0, 8)} (sig: ${activitySig?.slice(0, 8)}... not in trade sigs)`);
        }
      }
      return true;
    });

    console.log(`✅ Filtered activities: ${filteredActivities.length} (removed ${activities.length - filteredActivities.length} trade-related transfers)`);

    // Get unique mint addresses
    const mintAddresses = [...new Set(filteredActivities.map(a => a.mint_address).filter(Boolean))];
    console.log('🔑 Looking up pieces for mint addresses:', mintAddresses);
    
    // Fetch piece details for all mint addresses
    const { data: pieces, error: piecesError } = await supabase
      .from('pieces')
      .select('mint_address, id, name, preview_glb_url, thumbnail_url, poster_url, category')
      .in('mint_address', mintAddresses);

    if (piecesError) {
      console.error('❌ Error fetching pieces for NFT activities:', piecesError);
      console.error('Error details:', piecesError.message, piecesError.details);
    } else {
      console.log('📦 Fetched', pieces?.length || 0, 'pieces for NFT activities:', pieces);
    }

    // Create a map of mint_address to piece data
    const piecesMap = new Map();
    (pieces || []).forEach(piece => {
      console.log(`📸 Piece "${piece.name}":`, {
        thumbnail_url: piece.thumbnail_url || 'null',
        poster_url: piece.poster_url || 'null',
        preview_glb_url: piece.preview_glb_url || 'null',
        metadata: piece.metadata || 'null'
      });
      piecesMap.set(piece.mint_address, piece);
    });

    // Enrich activities with piece data
    return filteredActivities.map(activity => {
      const piece = piecesMap.get(activity.mint_address);
      return formatNFTActivity({ ...activity, pieces: piece });
    });
  } catch (error) {
    console.error('Exception in fetchNFTActivities:', error);
    return [];
  }
}

/**
 * Format NFT activity from blockchain
 */
function formatNFTActivity(activity) {
  const piece = activity.pieces;
  
  // Try multiple timestamp fields for backward compatibility
  // event_timestamp is stored as Unix timestamp (seconds), so multiply by 1000 for milliseconds
  let timestamp = activity.event_timestamp 
    ? (activity.event_timestamp * 1000) // Convert seconds to milliseconds
    : (activity.block_time ? new Date(activity.block_time).getTime() : (activity.created_at ? new Date(activity.created_at).getTime() : Date.now()));
  
  // Validate timestamp - if it's before 2020 or in the future, use fallback timestamps
  const year2020 = new Date('2020-01-01').getTime();
  const now = Date.now();
  if (timestamp < year2020 || timestamp > now + 86400000) { // More than 1 day in future
    // Use block_time or created_at as fallback
    timestamp = activity.block_time 
      ? new Date(activity.block_time).getTime() 
      : (activity.created_at ? new Date(activity.created_at).getTime() : now);
  }
  
  // Format piece name - fallback to mint address if no piece name
  const pieceName = piece?.name || (activity.mint_address ? `${activity.mint_address.slice(0, 4)}...${activity.mint_address.slice(-4)}` : 'Unknown Piece');
  
  // Get image URL from available sources and convert to public URL if needed
  let imageUrl = piece?.thumbnail_url 
    || piece?.poster_url
    || piece?.preview_glb_url 
    || '/images/piece-placeholder.png';
  
  // Convert storage path to public URL if it's not already a full URL
  if (imageUrl && !imageUrl.startsWith('/images/')) {
    imageUrl = convertToPublicUrl(imageUrl, STORAGE_BUCKETS.PREVIEWS);
  }
  
  console.log(`🖼️ Image URL for "${pieceName}":`, imageUrl, '| Original piece data:', {
    thumbnail_url: piece?.thumbnail_url,
    poster_url: piece?.poster_url,
    preview_glb_url: piece?.preview_glb_url
  });
  
  const baseActivity = {
    id: `nft_${activity.id}`,
    timestamp,
    // Use the converted timestamp (in milliseconds) for timeAgo calculation
    timeAgo: formatTimeAgo(timestamp),
    from: activity.from_address,
    to: activity.to_address,
    transactionHash: activity.tx_sig || activity.signature,
    pieceId: piece?.id || activity.mint_address,
    mintAddress: activity.mint_address,
    pieceName,
    image: imageUrl,
    collection: piece?.category || 'Unknown Collection'
  };

  // Map event types to our activity types
  switch (activity.event_type) {
    case 'mint':
      return {
        ...baseActivity,
        type: 'PIECE_CREATED',
        title: `Minted "${piece?.name || 'Unknown Piece'}"`,
        description: `New piece minted on Solana blockchain`,
        user: activity.to_address,
        quantity: '1 piece'
      };
    
    case 'transfer':
      return {
        ...baseActivity,
        type: 'PIECE_TRADED',
        title: 'Piece transferred',
        description: `Transferred from ${formatWallet(activity.from_address)} to ${formatWallet(activity.to_address)}`,
        user: activity.from_address,
        quantity: '1 piece'
      };
    
    case 'sale':
      const solAmount = activity.amount_lamports ? activity.amount_lamports / 1e9 : (activity.price || 0);
      return {
        ...baseActivity,
        type: 'PIECE_SOLD',
        title: `"${piece?.name || 'Piece'}" sold`,
        description: `Sold to ${formatWallet(activity.to_address)}`,
        user: activity.from_address,
        buyer: activity.to_address,
        price: `${solAmount.toFixed(2)} SOL`,
        usdPrice: `$${(solAmount * 150).toFixed(2)}`, // Placeholder, will be updated with real SOL price
        quantity: '1 piece'
      };
    
    case 'list':
      const listPrice = activity.amount_lamports ? activity.amount_lamports / 1e9 : (activity.price || 0);
      return {
        ...baseActivity,
        type: 'PIECE_LISTED',
        title: `"${piece?.name || 'Piece'}" listed for sale`,
        description: `Listed for ${listPrice.toFixed(2)} SOL`,
        user: activity.from_address,
        price: `${listPrice.toFixed(2)} SOL`,
        usdPrice: `$${(listPrice * 150).toFixed(2)}`,
        quantity: '1 piece'
      };
    
    default:
      return {
        ...baseActivity,
        type: 'PIECE_CREATED',
        title: piece?.name || 'Activity',
        description: `${activity.event_type} event`,
        user: activity.from_address || activity.to_address,
        quantity: '1 piece'
      };
  }
}

/**
 * Fetch wallet activities (app-level actions)
 */
async function fetchWalletActivities(limit = 50) {
  try {
    const { data: activities, error } = await supabase
      .from('wallet_activity')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) {
      console.error('Error fetching wallet activities:', error);
      return [];
    }

    if (!activities || activities.length === 0) {
      return [];
    }

    // Fetch executed trade signatures to filter out wallet activities that are part of P2P trades
    // These will be shown as consolidated trade activities instead
    const { data: executedTrades } = await supabase
      .from('p2p_trades')
      .select('execution_signature')
      .eq('status', 'executed')
      .not('execution_signature', 'is', null);

    const tradeSignatures = new Set(
      (executedTrades || []).map(t => t.execution_signature).filter(Boolean)
    );

    console.log(`🔄 Found ${tradeSignatures.size} executed trade signatures to filter from wallet activities`);

    // Filter out wallet activities that are part of P2P trades OR offer transactions
    const filteredActivities = activities.filter(activity => {
      // Check if this is a piece_traded activity with a signature matching an executed trade
      if (activity.activity_type === 'piece_traded' && activity.metadata?.signature) {
        if (tradeSignatures.has(activity.metadata.signature)) {
          console.log(`🚫 Filtering out wallet activity for ${activity.wallet?.slice(0, 8)} (part of P2P trade, sig: ${activity.metadata.signature?.slice(0, 8)}...)`);
          return false;
        }
      }
      
      // Filter out offer-related wallet activities (offer_made, offer_accepted, piece_purchased_via_offer, piece_sold_via_offer)
      // These are redundant with nft_activity sale records and offers table
      // Only show the final sale when transaction is actually executed
      if (activity.activity_type === 'piece_purchased_via_offer' || 
          activity.activity_type === 'piece_sold_via_offer' ||
          activity.activity_type === 'offer_made' ||
          activity.activity_type === 'offer_accepted') {
        console.log(`🚫 Filtering out wallet activity for ${activity.wallet?.slice(0, 8)} (offer-related, covered by offers table/nft_activity)`);
        return false;
      }
      
      return true;
    });

    console.log(`✅ Filtered wallet activities: ${filteredActivities.length} (removed ${activities.length - filteredActivities.length} trade/offer-related activities)`);

    // Extract piece IDs from metadata if available
    const pieceIds = [...new Set(
      filteredActivities
        .map(a => a.metadata?.pieceId || a.metadata?.piece_id)
        .filter(Boolean)
    )];

    console.log('📦 Wallet activities piece IDs:', pieceIds);

    // Fetch piece data if we have piece IDs
    let piecesMap = new Map();
    if (pieceIds.length > 0) {
      const { data: pieces, error: piecesError } = await supabase
        .from('pieces')
        .select('id, name, preview_glb_url, thumbnail_url, poster_url, category')
        .in('id', pieceIds);

      if (piecesError) {
        console.error('Error fetching pieces for wallet activities:', piecesError);
      } else {
        console.log('📦 Fetched pieces for wallet activities:', pieces);
        (pieces || []).forEach(piece => {
          piecesMap.set(piece.id, piece);
        });
      }
    }

    return filteredActivities.map(activity => {
      const pieceId = activity.metadata?.pieceId || activity.metadata?.piece_id;
      const piece = pieceId ? piecesMap.get(pieceId) : null;
      return formatWalletActivity(activity, piece);
    });
  } catch (error) {
    console.error('Exception in fetchWalletActivities:', error);
    return [];
  }
}

/**
 * Format wallet activity
 */
function formatWalletActivity(activity, piece = null) {
  const timestamp = new Date(activity.created_at).getTime();
  const metadata = activity.metadata || {};

  // Map activity types
  const typeMap = {
    'piece_created': 'PIECE_CREATED',
    'piece_sold': 'PIECE_SOLD',
    'piece_purchased': 'PIECE_SOLD',
    'piece_sold_via_offer': 'PIECE_SOLD',
    'piece_purchased_via_offer': 'PIECE_SOLD',
    'piece_listed': 'PIECE_LISTED',
    'capsule_created': 'COLLECTION_CREATED',
    'piece_traded': 'PIECE_TRADED'
  };

  const type = typeMap[activity.activity_type] || 'PIECE_CREATED';

  // Get image URL from piece data or metadata (check multiple sources)
  let imageUrl = piece?.thumbnail_url 
    || piece?.poster_url
    || metadata?.image
    || piece?.preview_glb_url 
    || '/images/piece-placeholder.png';
  
  // Convert storage path to public URL if needed
  if (imageUrl && !imageUrl.startsWith('/images/')) {
    imageUrl = convertToPublicUrl(imageUrl, STORAGE_BUCKETS.PREVIEWS);
  }

  // For capsule creation, wallet should be in "to" field (minted TO the wallet)
  // For other activities, wallet is in "from" field
  const isCapsuleCreation = activity.activity_type === 'capsule_created';
  
  return {
    id: `wallet_${activity.id}`,
    type,
    timestamp,
    timeAgo: formatTimeAgo(activity.created_at),
    user: activity.wallet,
    from: isCapsuleCreation ? null : activity.wallet,
    to: isCapsuleCreation ? activity.wallet : null,
    title: metadata.title || activity.activity_type,
    description: metadata.description || '',
    pieceId: piece?.id || metadata.pieceId || metadata.piece_id,
    pieceName: piece?.name || metadata.piece_name || metadata.pieceName,
    collection: piece?.category || metadata.collection,
    image: imageUrl,
    price: metadata.price,
    usdPrice: metadata.usdPrice,
    quantity: metadata.quantity || '1 piece',
    pieceCount: metadata.piece_count, // For capsule creation activities
    collectionId: metadata.capsule_id, // Capsule ID for navigation
    transactionHash: metadata.transactionHash
  };
}

/**
 * Fetch offer activities
 */
async function fetchOfferActivities(limit = 50) {
  try {
    // Fetch offers - only show 'active' offers being created, not accepted/executed ones
    // Accepted/executed offers are already shown as sales in nft_activity
    const { data: offers, error: offersError } = await supabase
      .from('offers')
      .select('*')
      .eq('status', 'active')  // Only show active offers, not accepted/executed
      .order('created_at', { ascending: false })
      .limit(limit);

    if (offersError) {
      console.error('Error fetching offers:', offersError);
      return [];
    }

    if (!offers || offers.length === 0) {
      return [];
    }

    // Get unique piece IDs
    const pieceIds = [...new Set(offers.map(o => o.piece_id).filter(Boolean))];
    
    // Fetch piece details for all piece IDs
    const { data: pieces, error: piecesError } = await supabase
      .from('pieces')
      .select('id, name, preview_glb_url, thumbnail_url, poster_url, category')
      .in('id', pieceIds);

    if (piecesError) {
      console.error('Error fetching pieces for offers:', piecesError);
      console.error('Error details:', piecesError.message, piecesError.details);
    }

    console.log('📦 Fetched pieces for offers:', pieces);

    // Create a map of piece_id to piece data
    const piecesMap = new Map();
    (pieces || []).forEach(piece => {
      console.log(`📸 Piece "${piece.name}": thumbnail_url=${piece.thumbnail_url || 'null'}, preview_glb_url=${piece.preview_glb_url || 'null'}`);
      piecesMap.set(piece.id, piece);
    });

    // Enrich offers with piece data
    return offers.map(offer => {
      const piece = piecesMap.get(offer.piece_id);
      return formatOfferActivity({ ...offer, pieces: piece });
    });
  } catch (error) {
    console.error('Exception in fetchOfferActivities:', error);
    return [];
  }
}

/**
 * Format offer activity
 */
function formatOfferActivity(offer) {
  const piece = offer.pieces;
  const timestamp = new Date(offer.created_at).getTime();
  // offer_price is already in SOL, not lamports
  const solAmount = offer.offer_price || 0;

  // Get image URL from available sources (check multiple locations)
  let imageUrl = piece?.thumbnail_url 
    || piece?.poster_url
    || piece?.preview_glb_url 
    || '/images/piece-placeholder.png';
  
  // Convert storage path to public URL if needed
  if (imageUrl && !imageUrl.startsWith('/images/')) {
    imageUrl = convertToPublicUrl(imageUrl, STORAGE_BUCKETS.PREVIEWS);
  }

  // Only showing active offers now (accepted/executed are shown as sales in nft_activity)
  return {
    id: `offer_${offer.id}`,
    type: 'OFFER_MADE',
    timestamp,
    timeAgo: formatTimeAgo(offer.created_at),
    user: offer.offerer_wallet,
    from: offer.offerer_wallet,
    to: offer.piece_owner_wallet,
    title: `Offer made on "${piece?.name || 'Piece'}"`,
    description: `Offered ${solAmount.toFixed(2)} SOL`,
    pieceId: offer.piece_id,
    pieceName: piece?.name || 'Unknown Piece',
    collection: piece?.category || 'Unknown Collection',
    image: imageUrl,
    price: `${solAmount.toFixed(2)} SOL`,
    usdPrice: `$${(solAmount * 150).toFixed(2)}`,
    quantity: '1 piece',
    transactionHash: offer.execution_signature
  };
}

/**
 * Fetch P2P trade activities
 */
async function fetchTradeActivities(limit = 50) {
  try {
    const { data: trades, error } = await supabase
      .from('p2p_trades')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) {
      console.error('Error fetching trade activities:', error);
      return [];
    }

    const executedTrades = (trades || []).filter(trade => trade.status === 'executed');
    
    // Get all mint addresses from trades to fetch piece details
    const mintAddresses = new Set();
    executedTrades.forEach(trade => {
      if (trade.initiator_pieces) {
        trade.initiator_pieces.forEach(p => {
          // Extract mint_address if p is an object, otherwise use p directly
          const mintAddress = typeof p === 'object' ? (p.mint_address || p.id) : p;
          if (mintAddress) mintAddresses.add(mintAddress);
        });
      }
      if (trade.counterparty_pieces) {
        trade.counterparty_pieces.forEach(p => {
          // Extract mint_address if p is an object, otherwise use p directly
          const mintAddress = typeof p === 'object' ? (p.mint_address || p.id) : p;
          if (mintAddress) mintAddresses.add(mintAddress);
        });
      }
    });

    console.log('🔍 Fetching pieces for mint addresses:', Array.from(mintAddresses));
    
    // Fetch piece details by mint_address
    let piecesMap = new Map();
    if (mintAddresses.size > 0) {
      const { data: pieces, error: piecesError } = await supabase
        .from('pieces')
        .select('id, mint_address, name, preview_glb_url, thumbnail_url, poster_url, category')
        .in('mint_address', Array.from(mintAddresses));
      
      if (piecesError) {
        console.error('❌ Error fetching pieces for trades:', piecesError);
      } else {
        console.log('✅ Fetched pieces for trades:', pieces);
        // Map by both mint_address AND id for flexible lookup
        (pieces || []).forEach(piece => {
          piecesMap.set(piece.mint_address, piece);
          piecesMap.set(piece.id, piece);
        });
      }
    }
    
    return executedTrades.map(trade => formatTradeActivity(trade, piecesMap));
  } catch (error) {
    console.error('Exception in fetchTradeActivities:', error);
    return [];
  }
}

/**
 * Format trade activity
 */
function formatTradeActivity(trade, piecesMap = new Map()) {
  const timestamp = new Date(trade.executed_at || trade.created_at).getTime();
  
  // Get all pieces involved in the trade
  const initiatorPieces = (trade.initiator_pieces || [])
    .map(p => {
      // Extract mint_address or id to look up in piecesMap
      const lookupKey = typeof p === 'object' ? (p.mint_address || p.id) : p;
      const piece = piecesMap.get(lookupKey);
      if (!piece) {
        console.warn('⚠️ Could not find piece for lookup key:', lookupKey, 'in piecesMap');
      }
      return piece;
    })
    .filter(Boolean);
  
  const counterpartyPieces = (trade.counterparty_pieces || [])
    .map(p => {
      // Extract mint_address or id to look up in piecesMap
      const lookupKey = typeof p === 'object' ? (p.mint_address || p.id) : p;
      const piece = piecesMap.get(lookupKey);
      if (!piece) {
        console.warn('⚠️ Could not find piece for lookup key:', lookupKey, 'in piecesMap');
      }
      return piece;
    })
    .filter(Boolean);
  
  // Get images for both sides
  const initiatorImage = initiatorPieces[0] ? (
    initiatorPieces[0].thumbnail_url 
    || initiatorPieces[0].poster_url
    || initiatorPieces[0].preview_glb_url 
    || '/images/piece-placeholder.png'
  ) : '/images/piece-placeholder.png';
  
  const counterpartyImage = counterpartyPieces[0] ? (
    counterpartyPieces[0].thumbnail_url 
    || counterpartyPieces[0].poster_url
    || counterpartyPieces[0].preview_glb_url 
    || '/images/piece-placeholder.png'
  ) : '/images/piece-placeholder.png';
  
  // Convert to public URLs
  const initiatorImageUrl = initiatorImage.startsWith('/images/') 
    ? initiatorImage 
    : convertToPublicUrl(initiatorImage, STORAGE_BUCKETS.PREVIEWS);
    
  const counterpartyImageUrl = counterpartyImage.startsWith('/images/')
    ? counterpartyImage
    : convertToPublicUrl(counterpartyImage, STORAGE_BUCKETS.PREVIEWS);
  
  // Create piece names string
  const initiatorPieceNames = initiatorPieces.map(p => p.name).join(', ') || 'Unknown';
  const counterpartyPieceNames = counterpartyPieces.map(p => p.name).join(', ') || 'Unknown';
  
  const formattedActivity = {
    id: `trade_${trade.id}`,
    type: 'PIECE_TRADED',
    timestamp,
    timeAgo: formatTimeAgo(trade.executed_at || trade.created_at),
    user: trade.initiator_wallet,
    from: trade.initiator_wallet,
    to: trade.counterparty_wallet,
    title: 'Trade executed',
    pieceName: `${initiatorPieceNames} ⇄ ${counterpartyPieceNames}`,
    description: `Trade between ${formatWallet(trade.initiator_wallet)} and ${formatWallet(trade.counterparty_wallet)}`,
    quantity: `${(trade.initiator_pieces?.length || 0) + (trade.counterparty_pieces?.length || 0)} pieces`,
    transactionHash: trade.execution_signature,
    image: initiatorImageUrl,
    // Store both images for the display
    tradeImages: {
      initiator: initiatorImageUrl,
      counterparty: counterpartyImageUrl
    },
    tradePieces: {
      initiator: initiatorPieces,
      counterparty: counterpartyPieces
    },
    collection: 'P2P Trade'
  };

  console.log('📦 Formatted trade activity:', {
    id: formattedActivity.id,
    hasTradeImages: !!formattedActivity.tradeImages,
    hasTradePieces: !!formattedActivity.tradePieces,
    initiatorPiecesCount: initiatorPieces.length,
    counterpartyPiecesCount: counterpartyPieces.length,
    initiatorImage: initiatorImageUrl,
    counterpartyImage: counterpartyImageUrl
  });

  return formattedActivity;
}

/**
 * Format wallet address for display
 */
function formatWallet(wallet) {
  if (!wallet) return 'Unknown';
  if (wallet.length < 12) return wallet;
  return `${wallet.slice(0, 4)}...${wallet.slice(-4)}`;
}

/**
 * Enrich activities with real-time SOL price
 */
export async function enrichActivitiesWithPrice(activities, solPrice) {
  if (!solPrice) return activities;

  return activities.map(activity => {
    if (activity.price && typeof activity.price === 'string' && activity.price.includes('SOL')) {
      const solAmount = parseFloat(activity.price.replace(' SOL', ''));
      return {
        ...activity,
        usdPrice: `$${(solAmount * solPrice).toFixed(2)}`
      };
    }
    return activity;
  });
}

/**
 * Filter activities by type
 */
export function filterActivitiesByType(activities, types) {
  if (!types || types.length === 0 || types.includes('all')) {
    return activities;
  }
  
  return activities.filter(activity => types.includes(activity.type));
}

/**
 * Filter activities by time range
 */
export function filterActivitiesByTime(activities, timeRange) {
  if (!timeRange || timeRange === 'all') {
    return activities;
  }

  const now = Date.now();
  const timeRanges = {
    '1h': 60 * 60 * 1000,
    '24h': 24 * 60 * 60 * 1000,
    '7d': 7 * 24 * 60 * 60 * 1000,
    '30d': 30 * 24 * 60 * 60 * 1000,
    '90d': 90 * 24 * 60 * 60 * 1000,
    '1y': 365 * 24 * 60 * 60 * 1000
  };

  const timeLimit = timeRanges[timeRange];
  if (!timeLimit) return activities;

  return activities.filter(activity => now - activity.timestamp <= timeLimit);
}

